import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { useAppDispatch } from "../hooks";
import {
  fetchUserAndBooksByNameAndMemberNo,
  resetUser,
  selectPRBSlice,
} from "../slices/personalRecommendBookSlice";
import { libraryParams } from "../types/Library";
import {
  completeInitialDirectRecommendAttempt,
  getInitialDirectRecommendAttempt,
  isRetryableDirectRecommendResponse,
  startInitialDirectRecommendRequest,
} from "../utils/directRecommendQuery";

const DIRECT_RECOMMEND_REQUEST_ERROR =
  "통신이 불안정합니다. 잠시 후 다시 시도해주세요.";

const clearRecommendSession = () => {
  window.sessionStorage.removeItem("data");
  window.sessionStorage.removeItem("library");
};

export const useAsanDirectRecommendQuery = () => {
  const { library } = useParams() as libraryParams;
  const { pathname } = useLocation();
  const { failedLogin } = useSelector(selectPRBSlice);
  const dispatch = useAppDispatch();

  const [attempt] = useState(() => getInitialDirectRecommendAttempt(library, pathname));
  const isDirectRecommendQuery = !!attempt;

  const [localError] = useState(attempt?.kind === "invalid" ? attempt.errorMessage : "");
  const [requestError, setRequestError] = useState(
    attempt?.kind === "valid" && attempt.status === "retryable"
      ? DIRECT_RECOMMEND_REQUEST_ERROR
      : ""
  );
  const [loading, setLoading] = useState(
    attempt?.kind === "valid" && ["ready", "in-flight"].includes(attempt.status)
  );
  const [canRetry, setCanRetry] = useState(
    attempt?.kind === "valid" && attempt.status === "retryable"
  );
  const requestRunIdRef = useRef(0);

  const executeDirectRecommendRequest = useCallback(() => {
    if (!attempt || attempt.kind !== "valid") return;

    const requestRunId = ++requestRunIdRef.current;

    clearRecommendSession();
    dispatch(resetUser());
    window.sessionStorage.setItem("library", JSON.stringify(attempt.normalizedLibrary));
    setRequestError("");
    setCanRetry(false);
    setLoading(true);

    startInitialDirectRecommendRequest(attempt, (payload) => {
      const directRecommendRequest = dispatch(fetchUserAndBooksByNameAndMemberNo(payload));
      attempt.abortRequest = directRecommendRequest.abort;

      return directRecommendRequest
        .unwrap()
        .then((response) => {
          if (isRetryableDirectRecommendResponse(response)) {
            throw new Error(DIRECT_RECOMMEND_REQUEST_ERROR);
          }
          return response;
        });
    })
      .catch(() => {
        if (requestRunId !== requestRunIdRef.current) return;
        setRequestError(DIRECT_RECOMMEND_REQUEST_ERROR);
        setCanRetry(attempt.status === "retryable");
      })
      .finally(() => {
        if (requestRunId === requestRunIdRef.current) setLoading(false);
      });
  }, [attempt, dispatch]);

  useEffect(() => {
    if (!attempt) return;

    if (attempt.kind === "invalid") {
      clearRecommendSession();
      dispatch(resetUser());
      completeInitialDirectRecommendAttempt(attempt);
      return;
    }

    if (["ready", "in-flight"].includes(attempt.status)) {
      executeDirectRecommendRequest();
    } else if (attempt.status === "retryable") {
      setRequestError(DIRECT_RECOMMEND_REQUEST_ERROR);
      setCanRetry(true);
      setLoading(false);
    } else {
      setRequestError("");
      setCanRetry(false);
      setLoading(false);
    }

    return () => {
      requestRunIdRef.current += 1;
    };
  }, [attempt, dispatch, executeDirectRecommendRequest]);

  return {
    directRecommendError: localError || requestError || failedLogin,
    directRecommendLoading: loading,
    isDirectRecommendQuery,
    canRetryDirectRecommend: canRetry,
    retryDirectRecommend: executeDirectRecommendRequest,
  };
};
